import type { ServerListItem } from '@/types/fleet';

/**
 * Audience grouping by censorship pressure. Mirrors the backend's
 * REALITY_PREFERRED_COUNTRY_CODES (backend/api .../vpn-connection/protocol-selection.ts):
 * users in these high-censorship countries are auto-routed to stealth
 * (VLESS-Reality) servers, so the fleet must keep stealth coverage for them.
 *
 * Reality is a *global* edge pool — the edges sit OUTSIDE these countries (you
 * can't reliably host inside them), so an audience is "covered" when the fleet
 * has at least one stealth-capable server anywhere, not one per country.
 */
export const HIGH_CENSORSHIP_AUDIENCES: readonly { code: string; name: string }[] = [
  { code: 'IR', name: 'Iran' },
  { code: 'CN', name: 'China' },
  { code: 'RU', name: 'Russia' },
];

/** A server that can carry stealth / VLESS-Reality traffic. */
export function isStealthServer(server: ServerListItem): boolean {
  return (server.capabilities ?? []).includes('stealth');
}

export interface AudienceCoverage {
  /** Stealth-capable servers across the whole fleet (the Reality edge pool). */
  stealthServers: ServerListItem[];
  /** Each high-censorship audience + whether it currently has stealth coverage. */
  audiences: { code: string; name: string; covered: boolean }[];
  /** True once the fleet has >= 1 stealth server (so every audience is covered). */
  covered: boolean;
}

export function computeAudienceCoverage(servers: ServerListItem[]): AudienceCoverage {
  const stealthServers = servers.filter(isStealthServer);
  const covered = stealthServers.length > 0;

  return {
    stealthServers,
    audiences: HIGH_CENSORSHIP_AUDIENCES.map((audience) => ({ ...audience, covered })),
    covered,
  };
}
